import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { Popper } from "@remo-co/ui-core/src/components/Popper";
import { Typography } from "@remo-co/ui-core/src/components/Typography";
import { Card } from "@remo-co/ui-core/src/components/Card";
import { Container } from "@remo-co/ui-core/src/components/Container";
import classNames from "classnames";
import { useI18n } from "i18n";
import { useCheckEventStatus } from "modules/event/hooks/useCheckEventStatus";
import { selectIsEventManager } from "modules/event/selectors";
import { IEventDirectoryPopulated } from "modules/eventDirectory/types";
import { selectIsMobile } from "modules/deviceInfo";
import { useStyles } from "./styles";
import BlankState from "../EventSwitcher/BlankState";
import MenuItem from "../EventSwitcher/MenuItem";

interface IMenuProps {
  containerRef: React.MutableRefObject<HTMLDivElement | null>;
  eventDirectory: IEventDirectoryPopulated;
  currentEventId: string;
  isMenuVisible: boolean;
  presentationMode?: boolean;
}

const Menu = ({
  containerRef,
  eventDirectory,
  currentEventId,
  isMenuVisible,
  presentationMode,
}: IMenuProps): JSX.Element => {
  const { t } = useI18n(["eventDirectory"]);
  const styles = useStyles();
  const isMobileView = useSelector(selectIsMobile);
  const isEventManager = useSelector(selectIsEventManager);
  const { checkIfEventHappeningNow, checkIfEventIsUpcoming } =
    useCheckEventStatus();

  const availableEvents = useMemo(
    () =>
      eventDirectory.events.filter(
        (event) =>
          event.id !== currentEventId &&
          (checkIfEventHappeningNow(event) ||
            (isEventManager && checkIfEventIsUpcoming(event))),
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [eventDirectory, isMenuVisible],
  );

  return (
    <Popper
      anchorEl={containerRef.current}
      popperOptions={{
        modifiers: [
          {
            name: "offset",
            options: {
              // eslint-disable-next-line no-nested-ternary
              offset: isMobileView
                ? [0, 4]
                : presentationMode
                ? [0, 2]
                : [10, 0],
              arrow: {
                enabled: false,
              },
            },
          },
        ],
      }}
      placement="bottom-start"
      open={isMenuVisible}
      disablePortal
      className={classNames(styles.eventPopper, {
        [styles.eventPopperMobile]: isMobileView,
        [styles.eventPopperMobilePresentation]:
          isMobileView && presentationMode,
      })}
    >
      <Card
        size="xs"
        flex={false}
        fullWidth={false}
        className={classNames(styles.card, {
          [styles.cardMenuVisible]: isMenuVisible,
          [styles.cardPresentationMode]: presentationMode,
          [styles.cardMobile]: isMobileView,
        })}
      >
        {availableEvents.length !== 0 && (
          <Container className={styles.container}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={classNames(styles.goToRelatedEvents, {
                [styles.goToRelatedEventsPresentationMode]: presentationMode,
              })}
            >
              {t("eventDirectory:go.to.related.events")}
            </Typography>
          </Container>
        )}

        {availableEvents.map((event) => (
          <MenuItem
            key={event.id}
            event={event}
            currentEventId={currentEventId}
            presentationMode={presentationMode}
            menuDialogStyle={styles.dialog}
          />
        ))}
        {availableEvents.length === 0 && (
          <BlankState presentationMode={presentationMode} />
        )}
      </Card>
    </Popper>
  );
};

export default Menu;
